import React from 'react';
import {Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {Location} from '@reach/router';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Footer from 'components/Footer';
import {OptinModalTrigger} from 'components/OptinModal';
import OptinFormChallenge from 'components/OptinFormChallenge/OptinFormChallenge';
import OptinFormChallengeModal from 'components/OptinFormChallenge/OptinFormChallengeModal';
import CountdownTimer from 'components/Elements/CountdownTimer';
import ScrollingVideo from 'components/ComponentLibrary/ScrollingVideo';
import FaqListCL from 'components/ComponentLibrary/FaqListCL/FaqListCL';
import ImageCardCL from 'components/ComponentLibrary/ImageCardCL';
import ReactMarkdown from 'react-markdown';

//@ts-ignore
import {cohosts, faqs} from 'data/wlc-lc.json';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import ExitModal from 'components/ComponentLibrary/ExitModal';
const {SF_API_URL} = process.env;
const {ORDER_API_URL} = process.env;
const {SITE_ID} = process.env;
const {LIVE_CHALLENGE_OPTIN_FORM_ID} = process.env;
const {LIVE_CHALLENGE_ORDER_OFFER_ID} = process.env;
const {LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID} = process.env;

export default function (props) {
  return <Location>{({location}) => <ChallengeLanding location={location} {...props} />}</Location>;
}

function ChallengeLanding({
  entry = 'default',
  variant = 'default',
  noExitIntent = false,
  noFixedVideo = false,
  optinOverride = false,
  pageTitle = '30 ways to make 2025 your year for effortless health',
  pageDescription = 'What if the foods you DO eat matter even more than the ones you swear off? Something as simple as adding more plant-powered superfoods to your meals makes all the difference for your gut, heart, and overall health. Plus, easy-to-make recipes fill you up, so you naturally “crowd out” tempting but troublesome choices. That’s why you’re invited to join us for this free, easy, and delicious challenge: Discover how to effortlessly add 30 plants to your diet… in just 3 days!',
  ogUrl = 'https://wlc.foodrevolution.org/challenge/',
  ogImage = 'https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/wlc/challenge/og/wlc-lc-open_graph-202501.jpg',
  location = {},
  ...props
}) {
  const {
    phases: {
      challenge: challengeSchedule = {
        phase: 'before',
        salesEndDate: ''
      }
    }
  } = usePhaseState();
  const phase = challengeSchedule.phase;
  const registrationDate = challengeSchedule.challengeRegistrationEnd;
  const registrationTime = new Date(registrationDate).getTime();

  const wlcCustomer = entry === 'wlcCustomer';
  const challengeName = 'Powered by Plants: A 30 Plants in 3 Days Challenge';
  const challengeShortName = '30 Plants in 3 Days Challenge';
  function Logo({style = 'color', maxHeight = 'none', className = undefined}) {
    return (
      <div className={classNames(`wlc-challenge-logo`, className)}>
        <img
          src={
            {
              color: 'https://cdn.foodrevolution.org/wlc/challenge/wlc-logo-challenge-pbp-color.svg',
              black: 'https://cdn.foodrevolution.org/wlc/challenge/wlc-logo-challenge-pbp-black.svg',
              white: 'https://cdn.foodrevolution.org/wlc/challenge/wlc-logo-challenge-pbp-white.svg'
            }[style]
          }
          alt="WHOLE Life club challenge logo for powered by plants"
          style={{maxHeight: maxHeight}}
        />
      </div>
    );
  }

  const headerImageUrl = 'https://cdn.foodrevolution.org/wlc/challenge/images/1203599963-lg.jpg';
  const headerImageAlt = 'food background with assortment of fresh organic fruits and vegetables';

  function Timer({headlineClass = undefined, className = undefined}) {
    return (
      <div className={classNames(`timer-wrap`, className)}>
        <h5 className={classNames(`text-uppercase`, `mt-0`, `mb-2`, headlineClass)}>Registration Closes In:</h5>
        <CountdownTimer time={registrationTime} />
      </div>
    );
  }
  useBalanceText();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} ogImage={ogImage} url={ogUrl} />
      <div className="page-content d-flex flex-column flex-grow-1">
        {phase === 'after' && !optinOverride ? (
          <>
            <SectionCL
              id="wlclc-header"
              color="black"
              className="text-center text-white d-flex flex-column flex-grow-1"
              innerWrapClass="d-flex flex-column flex-grow-1"
              innerClass="py-5 my-auto"
              imgUrl={headerImageUrl}
              imgDescription={headerImageAlt}
              angle="none"
              order="first"
            >
              <Container>
                <Row>
                  <Col>
                    <div className="background-black-90 rounded p-4 p-lg-5">
                      <h2 className="section-heading">Registration for the {challengeName} Has Ended</h2>
                      <h5 className="text-400 mt-0">
                        We’re sorry, but registration for the {challengeName} has closed.
                      </h5>
                    </div>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          </>
        ) : (
          <>
            <SectionCL
              id="wlclc-header"
              color="gray"
              imgUrl={headerImageUrl}
              imgDescription={headerImageAlt}
              angle="none"
              order="first"
              innerClass="pt-0"
            >
              <div className="header-cta-box background-black-80 py-4 text-center text-white">
                <Container>
                  <Row>
                    <Col>
                      <p className="text-uppercase mb-2">
                        <i>Free to all!</i>
                      </p>
                      {phase === 'during' && !optinOverride ? <Timer /> : null}
                    </Col>
                  </Row>
                </Container>
              </div>
              <Container className="header-cta-optin">
                <Row className="justify-content-center">
                  <Col xs="12" lg="10">
                    <div className="optin-box background-light-gray box-shadow-black-50 p-4 rounded">
                      <div className="text-center mb-3">
                        <Logo className="mb-4" maxHeight="150px" />
                        <p>January 13 – January 15, 2025</p>
                      </div>
                      <OptinFormChallenge
                        apiUrl={SF_API_URL}
                        orderApiUrl={ORDER_API_URL}
                        siteId={SITE_ID}
                        contactInfoFormHeader={
                          <div className="text-center">
                            <p className={classNames(`balance-text`, wlcCustomer && `mb-2`)}>
                              Enter your email below to join:
                            </p>
                            {wlcCustomer && (
                              <p className="balance-text text-small">
                                <b>Note:</b> Please use the same email that you use to log into WHOLE Life Club. If you
                                don’t use the same email, a new account, with new login credentials, will be created for
                                you.
                              </p>
                            )}
                          </div>
                        }
                        entry={entry}
                        optinFormId={LIVE_CHALLENGE_OPTIN_FORM_ID}
                        offerId={LIVE_CHALLENGE_ORDER_OFFER_ID}
                        offerPaymentOptionId={LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID}
                        navigateUrl={wlcCustomer ? '/challenge/wlc/welcome/' : '/challenge/welcome/'}
                        contactInfoFormButtonText="Register Now!"
                      />
                    </div>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="wlclc-video" color="light-gray" angle="both">
              <Container>
                <Row className="justify-content-center">
                  <Col xs="12" lg="10" className="video-col px-0 px-sm-3">
                    <ScrollingVideo
                      video={{
                        url: 'https://www.youtube.com/watch?v=wp1eByjkLHI',
                        label: 'WLC-LC - Landing Video',
                        thumbnail: 'https://cdn.foodrevolution.org/wlc/challenge/wlc-lc-thumb-landing-20241223.gif',
                        noButton: true
                      }}
                      text={
                        <>
                          <h5 className="section-heading text-white">Join the {challengeShortName}!</h5>
                        </>
                      }
                      button={{
                        scrollToSection: 'wlclc-cta-3',
                        text: 'Register Now!',
                        optin: true
                      }}
                      noFixed={false}
                    />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="wlclc-vibrant" color="green" className="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-only-heading">Set 2025 up to be your most vibrant year yet!</h3>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="wlclc-days"
              color="white"
              imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1036554940-square.jpg"
              imgDescription="woman picking groceries from produce section"
              imgPosition="right"
              angle="bottom"
            >
              <h3 className="section-heading">In Just 3 Days, You’ll:</h3>
              <IconListCL>
                <>Add 30 plant-based foods to your meals.</>
                <>Watch live recipe demos.</>
                <>Learn from world-renowned experts.</>
                <>Participate in Q&As and community challenges.</>
              </IconListCL>
            </SectionCL>

            <SectionCL
              id="wlclc-variety"
              color="light-gray"
              imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/2150857924-square.jpg"
              imgDescription="hands holding a bowl of healthy foods in front of healthy foods on a table"
              imgPosition="left"
            >
              <h3 className="section-heading">The Key to Health Is Variety.</h3>
              <p>A diet rich in diverse plant-based foods supports a:</p>
              <IconListCL>
                <b>Healthy gut</b>
                <b>Strong immune system</b>
                <b>Clear mind</b>
              </IconListCL>
              <p>
                Studies show that adding more plants to your plate can improve longevity, reduce disease risks, and help
                you feel amazing.
              </p>
            </SectionCL>

            <SectionCL
              id="wlclc-studies"
              color="white"
              imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/2154187191-square.jpg"
              imgDescription="younger man working with older man in the kitchen"
              imgPosition="right"
            >
              <h3 className="section-heading">
                Studies show adding more plants to your plate could add years to your life.
              </h3>
              <p>
                Most people severely lack the health-boosting nutrients their body needs to thrive. This can leave them
                with a range of problems, from unwanted weight gain to heart issues to cancer, poor gut health, a
                struggling immune system, and more.
              </p>
              <p>
                <b>Fortunately, you don’t have to be like most people!</b>
              </p>
            </SectionCL>

            <SectionCL id="wlclc-kickoff" color="green" className="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">
                      It’s critical to add a wide variety of plant-based foods to your diet
                    </h3>
                    <h3 className="m-0">That’s why we’re kicking off the {challengeShortName}!</h3>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="wlclc-challenge" color="white">
              <Container>
                <Row className="text-center mb-4">
                  <Col>
                    <h3 className="section-heading">How the Challenge Works</h3>
                    <p>Each day, we’ll show you easy and delicious ways to add more plants to your meals.</p>
                    <p>
                      Plus, you’ll get live recipe demonstrations, interviews with world-leading experts, Q&As, and
                      more.
                    </p>
                    <p>
                      Before you know it… You’ll have added 30 different nutrient-loaded plants to your plate (and to
                      your tummy!).
                    </p>
                  </Col>
                </Row>
                <Row className="justify-content-center">
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/global/recipes/12-Ingredient-Superfood-Salad-Bowl-4.jpg"
                    imageAlt=""
                    type="vertical"
                    verticalColLg="10"
                  >
                    <h5 className="section-heading">
                      <b>January 13th:</b> Inspire
                    </h5>
                    <p>Get excited about gut-healthy, plant-powered meals.</p>
                    <p>
                      <b>Special guest:</b> Dr. Brooke Goldner, MD, interviewed by Ocean Robbins.
                    </p>
                    <p>
                      <b>Featured recipe demo:</b> 12-Ingredient Superfood Salad Bowl with Creamy Citrus Dressing
                    </p>
                  </ImageCardCL>
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/global/recipes/Mulligatawany-soup-2.jpg"
                    imageAlt=""
                    type="vertical"
                    verticalColLg="10"
                  >
                    <h5 className="section-heading">
                      <b>January 14th:</b> Knowledge
                    </h5>
                    <p>Learn how plants can transform your health.</p>
                    <p>
                      <b>Special guest:</b> Nichole Dandrea-Russert, RDN, interviewed by Ocean Robbins.
                    </p>
                    <p>
                      <b>Featured recipe demo:</b> Mulligatawny Soup
                    </p>
                  </ImageCardCL>
                  <ImageCardCL
                    image="https://cdn.foodrevolution.org/global/recipes/Lentil-and-Quinoa-Stuffed-Peppers-3.jpg"
                    imageAlt=""
                    type="vertical"
                    verticalColLg="10"
                  >
                    <h5 className="section-heading">
                      <b>January 15th:</b> Action
                    </h5>
                    <p>
                      Turn your inspiration into lasting habits! Discover simple, effective ways to make these
                      plant-powered changes stick so you can truly feel the difference.
                    </p>
                    <p>
                      Hosted by Ocean and Nichole, along with Rosie Mackay, CNS, LDN, and Wilfredo Benitez, MS, MEd.
                    </p>
                    <p>
                      <b>Featured recipe demo:</b> Lentil and Quinoa Stuffed Peppers
                    </p>
                  </ImageCardCL>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="wlclc-cta-2"
              color="purple"
              imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/1500880730-square.jpg"
              imgDescription="smiling woman with a healthy salad bowl"
              imgPosition="right"
              className="text-center text-white"
            >
              <Container>
                <Row>
                  <Col>
                    <h2 className="text-uppercase mt-0 mb-4">Join the {challengeName}</h2>
                    <OptinModalTrigger btnText="Register Now!" />
                    {phase === 'during' && !optinOverride ? <Timer className="my-4" /> : null}
                    {entry === 'default' && (
                      <p className="balance-text text-small mt-4">
                        <i>
                          The event is TOTALLY FREE. And if you want to go further — you’ll get an invitation to join
                          FRN’s thriving membership community, WHOLE Life Club, with a $1 trial and a special discount.
                        </i>
                      </p>
                    )}
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="wlclc-about" color="white">
              <Container>
                <Row>
                  <Col>
                    <ImageCardCL
                      image="https://cdn.foodrevolution.org/wlc/challenge/images/1485675684.jpg"
                      imageAlt="woman using laptop surrounded by healthy food"
                    >
                      <h5 className="section-heading">Learn from Leading Health Experts</h5>
                      <p>
                        During the Challenge, you’ll learn from bestselling authors, researchers, and world-renowned
                        food experts as they share how to protect your health with the most colorful foods on the
                        planet.
                      </p>
                    </ImageCardCL>
                    <ImageCardCL
                      image="https://cdn.foodrevolution.org/wlc/challenge/images/2093871255.jpg"
                      imageAlt="close up of woman’s hands slicing fresh organic carrots on kitchen counter"
                    >
                      <h5 className="section-heading">Recipe Demos</h5>
                      <p>
                        Join our dietitians, Rosie and Nichole, as they demonstrate how to whip up colorful plant-based
                        dishes that will delight your taste buds.
                      </p>
                    </ImageCardCL>
                    <ImageCardCL
                      image="https://cdn.foodrevolution.org/wlc/challenge/images/1190205448.jpg"
                      imageAlt="senior couple cooking together in the kitchen"
                    >
                      <h5 className="section-heading">Two Daily Challenge Recipes</h5>
                      <p>
                        Each day offers two different recipes so you can choose which works best for you. Either way,
                        you’ll be doing your body a great big favor!
                      </p>
                    </ImageCardCL>
                    <ImageCardCL
                      image="https://cdn.foodrevolution.org/wlc/challenge/images/1599002142.jpg"
                      imageAlt="hands holding a bowl of pumpkin soup"
                    >
                      <h5 className="section-heading">Prizes</h5>
                      <p>
                        Post pictures of your meals for a chance to win prizes. Of course, just by participating in the
                        Powered by Plants: a 30 Plants in 3 Days Challenge, your health will have already won!
                      </p>
                    </ImageCardCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="wlclc-host"
              color="black"
              imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
              imgDescription="headshot of Ocean Robbins"
              imgPosition="right"
              className="text-white"
            >
              <h2 className="section-heading text-uppercase mb-2">Your Host</h2>
              <h3 className="section-heading">Ocean Robbins</h3>
              <p>
                Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                author, husband, father, gardener, and dancer. Born in a log cabin and raised on home-grown food, his
                work is rooted in his family’s health-conscious legacy. From founding the nonprofit YES! at age 16 to
                leading a global food revolution movement, Ocean embodies his commitment to healthy people and a healthy
                planet.
              </p>
              <p>
                Ocean’s best-selling books, <i>31-Day Food Revolution,</i> <i>Real Superfoods,</i> and{' '}
                <i>Powered By Plants,</i> offer practical guidance on adopting diets that benefit both human and
                planetary wellness. His influential TEDx talk, <i>Eating Your Way To Happiness,</i> has reached over a
                million viewers, and he’s organized and hosted dozens of successful online courses and Summits.
              </p>
              <p>
                Ocean’s been recognized with numerous prestigious awards, including a National Jefferson Award for
                Outstanding Public Service and the Freedom’s Flame Award. As a former adjunct professorship at Chapman
                University and the founding president of the nonprofit Food Revolution Alliance, Ocean is on a mission
                to transform our food system, drive down the chronic disease epidemic, and mobilize people worldwide to
                be part of the solution.
              </p>
            </SectionCL>

            <SectionCL id="wlclc-guest" color="green" className="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-only-heading text-uppercase">Guest Expert</h2>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="wlclc-brooke"
              color="light-gray"
              imgUrl="https://cdn.foodrevolution.org/global/speakers/frn-guest_expert-portrait-square-Brooke_goldner-2022.jpg"
              imgDescription="headshot of Brooke Goldner, MD"
              imgPosition="left"
            >
              <h3 className="section-heading">Brooke Goldner, MD</h3>
              <p>
                Dr. Goldner is a board-certified medical doctor and the author of multiple bestselling books, including{' '}
                <i>Goodbye Lupus, Goodbye Autoimmune Disease,</i> and her recently released recipe book,{' '}
                <i>Goodbye Lupus, Hello Delicious.</i>
              </p>
              <p>
                She has been featured in People Magazine, multiple documentaries like Disease Reversal Hope, & Eating
                You Alive, and television shows like the Hallmark Home & Family Show.
              </p>
              <p>
                She graduated from Carnegie Mellon University with Honors for genetic research in Leukemia and
                Neurobiology, graduated from Temple University School of Medicine and was Chief Resident at UCLA-Harbor
                Residency.
              </p>
              <p>
                She has published multiple case studies in fully reversing Lupus & Sjogren’s diseases and lupus
                nephritis with her nutrition protocol.
              </p>
              <p>
                She is the sole autoimmune professor for the Plant-Based Nutrition Certification from Cornell University
                and a member of the Forbes Health Advisory Board.
              </p>
            </SectionCL>

            <SectionCL id="wlclc-cohost" color="green" className="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-only-heading text-uppercase">Your Co-Hosts</h2>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            {cohosts.map((cohost, i) => (
              <SectionCL
                id={'wlclc-' + cohost.id}
                key={cohost.id}
                color="white"
                imgUrl={cohost.image.url}
                imgPosition={i % 2 ? 'left' : 'right'}
                angleBottomDisplay={i < 2 ? 'lg-none' : 'default'}
                innerClass={i > 0 ? 'section-padding-lg' : 'default'}
              >
                <h3 className="section-heading">{cohost.name}</h3>
                <ReactMarkdown source={cohost.bio} />
              </SectionCL>
            ))}

            <SectionCL id="wlclc-faq" color="light-gray">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">Frequently Asked Questions</h3>
                    <FaqListCL faqlist={faqs} />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="wlclc-cta-3"
              color="purple"
              imgUrl="https://cdn.foodrevolution.org/wlc/challenge/images/629353764-square.jpg"
              imgDescription="mother and daughter in the kitchen being silly"
              imgPosition="left"
              className="text-center text-white"
              order="last"
              angleBottomDisplay="none"
            >
              <Container>
                <Row>
                  <Col>
                    <h2 className="text-uppercase mt-0 mb-4">Join the {challengeName}</h2>
                    <OptinModalTrigger btnText="Register Now!" />
                    {phase === 'during' && !optinOverride ? <Timer className="my-4" /> : null}
                    {entry === 'default' && (
                      <p className="balance-text text-small mt-4">
                        <i>
                          The event is TOTALLY FREE. And if you want to go further — you’ll get an invitation to join
                          FRN’s thriving membership community, WHOLE Life Club, with a $1 trial and a special discount.
                        </i>
                      </p>
                    )}
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          </>
        )}
      </div>
      <OptinFormChallengeModal
        navigateUrl={wlcCustomer ? '/challenge/wlc/welcome/' : '/challenge/welcome/'}
        optinFormProps={{
          apiUrl: SF_API_URL,
          orderApiUrl: ORDER_API_URL,
          siteId: SITE_ID,
          contactInfoFormHeader: (
            <div className="text-center">
              <p className={classNames(`balance-text`, wlcCustomer && `mb-2`)}>Enter your email below to join:</p>
              {wlcCustomer && (
                <p className="balance-text text-small">
                  <b>Note:</b> Please use the same email that you use to log into WHOLE Life Club.{' '}
                  <br className="d-none d-xl-block" />
                  If you don’t use the same email, a new account, with new login credentials, will be created for you.
                  you.
                </p>
              )}
            </div>
          ),
          entry: entry,
          optinFormId: LIVE_CHALLENGE_OPTIN_FORM_ID,
          offerId: LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID,
          offerPaymentOptionID: LIVE_CHALLENGE_ORDER_OFFER_PAYMENT_OPTION_ID,
          contactInfoFormButtonText: 'Register Now!'
        }}
      />
      <ExitModal
        exitDelay={30000}
        content={
          <>
            <p>You’re just one FREE 3-day Challenge away from a healthier you.</p>
            <p>
              Join <b>{challengeName}!</b>
            </p>
          </>
        }
        positiveOptin
        positiveButtonText={<>Reserve my spot for the free Challenge!</>}
        negativeButtonText={<>No, thanks, I’m good.</>}
        image={{
          url: {
            default: 'https://cdn.foodrevolution.org/wlc/challenge/images/1346414784-exit.jpg',
            wlcCustomer: 'https://cdn.foodrevolution.org/wlc/challenge/images/1289287186-exit.jpg'
          }[entry],
          alt: {
            default: 'young man preparing meal in the kitchen',
            wlcCustomer: 'woman with arms open wide in public park on a beautiful autumn day'
          }[entry]
        }}
      />
      <Footer />
    </div>
  );
}
